/**
 * OwnerFilter Component
 * Multi-select filter for OKR owners
 */

import React, { useState } from 'react';
import { Search } from 'lucide-react';

interface Owner {
  id: string;
  name: string;
  /** Absent unless the caller is entitled to this owner's email address. */
  email?: string;
  department?: string;
}

interface OwnerFilterProps {
  availableOwners: Owner[];
  selectedOwnerIds: string[];
  onChange: (ownerIds: string[]) => void;
}

export const OwnerFilter: React.FC<OwnerFilterProps> = ({
  availableOwners,
  selectedOwnerIds,
  onChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const handleToggle = (ownerId: string) => {
    if (selectedOwnerIds.includes(ownerId)) {
      onChange(selectedOwnerIds.filter((id) => id !== ownerId));
    } else {
      onChange([...selectedOwnerIds, ownerId]);
    }
  };

  // `email` and `department` are optional — most callers no longer receive the
  // email at all, so both must be guarded before .toLowerCase().
  const normalizedQuery = searchQuery.toLowerCase();
  const filteredOwners = availableOwners.filter((owner) =>
    [owner.name, owner.email, owner.department].some((field) =>
      field?.toLowerCase().includes(normalizedQuery)
    )
  );

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-stone-700">
        Owner
      </label>

      {/* Search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-stone-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search owners..."
          className="block w-full pl-10 pr-3 py-1.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
      </div>

      {/* Owner List */}
      <div className="max-h-48 overflow-y-auto space-y-1 border border-stone-200 rounded-lg p-2">
        {filteredOwners.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">
            No owners found
          </div>
        ) : (
          filteredOwners.map((owner) => {
            const isSelected = selectedOwnerIds.includes(owner.id);
            return (
              <label
                key={owner.id}
                className="flex items-start gap-2 px-2 py-2 rounded hover:bg-stone-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(owner.id)}
                  className="mt-0.5 w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-2 focus:ring-primary-500 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900 truncate">
                    {owner.name}
                  </div>
                  {owner.email && (
                    <div className="text-xs text-stone-500 truncate">
                      {owner.email}
                    </div>
                  )}
                  {owner.department && (
                    <div className="text-xs text-stone-400 truncate">
                      {owner.department}
                    </div>
                  )}
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};
